import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import atributoService from '../services/atributoService';
import regimenLaboralService from '../services/regimenLaboralService';
import formulaService from '../services/formulaService';
import afpService from '../services/afpService';
import informacionGeneralService from '../services/informacionGeneralService';
import tramoRentaService from '../services/tramoRentaService';
import plameConfiguracionService from '../services/plameConfiguracionService';
import empleadoService from '../services/empleadoService';
import calculosBoletaService from '../services/calculosBoletaService';
import calculosBoletaInternaService from '../services/calculosBoletaInternaService';
import configuracionPlanillaAlternativaService from '../services/configuracionPlanillaAlternativaService';
import configuracionViaticosStandardService from '../services/configuracionViaticosStandardService';
import {
  TIPOS_CONTRATACION_EMPLEADO,
  obtenerTiposContratacionEmpleado,
  esTipoContratacionEmpleadoValido,
} from '../constants/tiposContratacionEmpleado';
import * as XLSX from 'xlsx';

function normalizarTipoPlanilla(valor) {
  const v = String(valor || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_');
  if (!v) return null;
  if (v === 'dual' || v.includes('dual') || v === 'plame_interna' || v === 'plame+interna') {
    return 'dual';
  }
  if (
    v === 'solo_interna' ||
    v === 'solointerna' ||
    v === 'interna' ||
    v.includes('solo_interna')
  ) {
    return 'solo_interna';
  }
  return null;
}

/**
 * Tipo de planilla efectivo de una fila del Excel.
 * "Interno" como tipo de contratación siempre implica solo_interna.
 */
function resolverTipoPlanilla(fila) {
  const contratacion = String(fila?.tipo_contratacion || '').trim();
  if (contratacion === 'Interno') return 'solo_interna';
  return normalizarTipoPlanilla(fila?.tipo_planilla) || 'dual';
}

function normalizarTipoBoleta(valor) {
  const v = String(valor || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_');
  if (!v) return null;
  if (v === 'plame' || v === 'oficial' || v === 'real' || v === 'declarado') return 'plame';
  if (v === 'interna' || v === 'interno' || v === 'control' || v === 'solo_interna') return 'interna';
  return null;
}

function buscarHoja(sheetNames, candidatos) {
  const lower = (sheetNames || []).map((n) => ({ raw: n, key: String(n).trim().toLowerCase() }));
  for (const cand of candidatos) {
    const hit = lower.find((s) => s.key === cand || s.key.replace(/\s+/g, '_') === cand);
    if (hit) return hit.raw;
  }
  return null;
}

const CAMPOS_FECHA_EMPLEADO = [
  'fecha_nacimiento',
  'fecha_ingreso',
  'fecha_ingreso_interna',
  'fecha_cese',
];

/**
 * El Excel puede traer fechas como texto (dd/mm/aaaa o aaaa-mm-dd), como objeto Date
 * o como número de serie de Excel. El backend solo acepta aaaa-mm-dd.
 * Devuelve null si no se puede interpretar.
 */
function normalizarFechaExcel(valor) {
  if (valor === null || valor === undefined || valor === '') return null;

  const aTexto = (fecha) => {
    if (Number.isNaN(fecha.getTime())) return null;
    const mes = String(fecha.getMonth() + 1).padStart(2, '0');
    const dia = String(fecha.getDate()).padStart(2, '0');
    return `${fecha.getFullYear()}-${mes}-${dia}`;
  };

  if (valor instanceof Date) return aTexto(valor);

  if (typeof valor === 'number' && Number.isFinite(valor)) {
    // Serie de Excel: día 1 = 1900-01-01, con el desfase histórico del año 1900
    const base = Date.UTC(1899, 11, 30);
    const fecha = new Date(base + Math.round(valor) * 86400000);
    const mes = String(fecha.getUTCMonth() + 1).padStart(2, '0');
    const dia = String(fecha.getUTCDate()).padStart(2, '0');
    return `${fecha.getUTCFullYear()}-${mes}-${dia}`;
  }

  const texto = String(valor).trim();
  if (!texto) return null;

  const iso = texto.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (iso) {
    return `${iso[1]}-${iso[2].padStart(2, '0')}-${iso[3].padStart(2, '0')}`;
  }

  const dmy = texto.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
  if (dmy) {
    return `${dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`;
  }

  const parseada = new Date(texto);
  return Number.isNaN(parseada.getTime()) ? null : aTexto(parseada);
}

/** CUSPP: quita espacios, guiones y caracteres invisibles que suele traer el Excel. */
function normalizarCuspp(valor) {
  return String(valor ?? '')
    .replace(/[^A-Za-z0-9]/g, '')
    .toUpperCase();
}

/** Documento en hojas de conceptos: acepta DNI o Carnet de Extranjería (número). */
function obtenerDocumentoConcepto(fila) {
  const valor =
    fila?.numero_documento ??
    fila?.DNI ??
    fila?.dni ??
    fila?.documento ??
    '';
  return String(valor).trim();
}

function esDocumentoConceptoValido(doc) {
  if (!doc) return false;
  // DNI: 8 dígitos; Carnet de Extranjería / Pasaporte: 6–20 alfanuméricos
  if (/^\d{8}$/.test(doc)) return true;
  if (/^[A-Za-z0-9]{6,20}$/.test(doc)) return true;
  return false;
}

const ImportarEmpleados = ({ onClose, onImportSuccess }) => {
  const { selectedCompany } = useAuth();
  const fileInputRef = useRef(null);
  
  // Estados para datos de las APIs
  const [datosApis, setDatosApis] = useState({
    atributos: {},
    regimenesLaborales: [],
    formulas: [],
    afps: [],
    informacionGeneral: {},
    tramosRenta: [],
    conceptosPlame: []
  });
  const [planillaAlternativaActiva, setPlanillaAlternativaActiva] = useState(false);
  const [viaticosStandardActivo, setViaticosStandardActivo] = useState(false);

  // Estados de carga
  const [cargandoDatos, setCargandoDatos] = useState(false);
  const [datosListos, setDatosListos] = useState(false);
  const [progresoCarga, setProgresoCarga] = useState(0);

  // Estados para el archivo
  const [archivo, setArchivo] = useState(null);
  const [procesandoArchivo, setProcesandoArchivo] = useState(false);
  const [datosExcel, setDatosExcel] = useState([]);
  const [conceptosExcel, setConceptosExcel] = useState([]);
  
  // Estados para validación e importación
  const [validaciones, setValidaciones] = useState([]);
  const [validacionesConceptos, setValidacionesConceptos] = useState([]);
  const [erroresValidacion, setErroresValidacion] = useState([]);
  const [erroresValidacionConceptos, setErroresValidacionConceptos] = useState([]);
  const [empleadosValidos, setEmpleadosValidos] = useState([]);
  const [conceptosValidos, setConceptosValidos] = useState([]);
  const [mostrarValidaciones, setMostrarValidaciones] = useState(false);
  const [importandoEmpleados, setImportandoEmpleados] = useState(false);

  // Estados para resultados
  const [resultadosImportacion, setResultadosImportacion] = useState(null);
  const [mostrarResultados, setMostrarResultados] = useState(false);
  
  // Cargar todos los datos necesarios al montar el componente
  useEffect(() => {
    if (selectedCompany?.id) {
      cargarTodosLosDatos();
    }
  }, [selectedCompany]);

  const cargarTodosLosDatos = async () => {
    setCargandoDatos(true);
    setProgresoCarga(0);
    
    try {
      console.log('🚀 Iniciando carga de todos los datos para importación...');
      
      // Array de tareas de carga
      const tareasDeCargas = [
        { nombre: 'Atributos del sistema', funcion: cargarAtributos },
        { nombre: 'Regímenes laborales', funcion: cargarRegimenesLaborales },
        { nombre: 'Fórmulas de cálculo', funcion: cargarFormulas },
        { nombre: 'Datos de AFPs', funcion: cargarAfps },
        { nombre: 'Información general (UIT/Sueldo)', funcion: cargarInformacionGeneral },
        { nombre: 'Tramos de renta', funcion: cargarTramosRenta },
        { nombre: 'Códigos PLAME', funcion: cargarCodigosPlame },
        { nombre: 'Planilla alternativa', funcion: cargarPlanillaAlternativa },
        { nombre: 'Viáticos standard', funcion: cargarViaticosStandard },
      ];

      let datosCompletos = {
        atributos: {},
        regimenesLaborales: [],
        formulas: [],
        afps: [],
        informacionGeneral: {},
        tramosRenta: [],
        conceptosPlame: []
      };

      // Ejecutar cada tarea de carga
      for (let i = 0; i < tareasDeCargas.length; i++) {
        const tarea = tareasDeCargas[i];
        console.log(`📋 Cargando: ${tarea.nombre}...`);
        
        try {
          const resultado = await tarea.funcion();
          
          // Asignar resultado según el tipo de tarea
          switch (i) {
            case 0: datosCompletos.atributos = resultado; break;
            case 1: datosCompletos.regimenesLaborales = resultado; break;
            case 2: datosCompletos.formulas = resultado; break;
            case 3: datosCompletos.afps = resultado; break;
            case 4: datosCompletos.informacionGeneral = resultado; break;
            case 5: datosCompletos.tramosRenta = resultado; break;
            case 6: datosCompletos.conceptosPlame = resultado; break;
            case 7:
              setPlanillaAlternativaActiva(Boolean(resultado));
              break;
            case 8:
              setViaticosStandardActivo(Boolean(resultado));
              break;
          }
          
          console.log(`✅ ${tarea.nombre} cargado exitosamente`);
        } catch (error) {
          console.error(`❌ Error cargando ${tarea.nombre}:`, error);
        }
        
        // Actualizar progreso
        const progreso = Math.round(((i + 1) / tareasDeCargas.length) * 100);
        setProgresoCarga(progreso);
      }

      setDatosApis(datosCompletos);
      setDatosListos(true);
      console.log('✅ Todos los datos cargados exitosamente');
      console.log('📊 Resumen de datos cargados:', {
        atributos: Object.keys(datosCompletos.atributos).length,
        regimenesLaborales: datosCompletos.regimenesLaborales.length,
        formulas: datosCompletos.formulas.length,
        afps: datosCompletos.afps.length,
        informacionGeneral: Object.keys(datosCompletos.informacionGeneral).length,
        tramosRenta: datosCompletos.tramosRenta.length,
        conceptosPlame: datosCompletos.conceptosPlame.length
      });
      console.log('🔍 ESTRUCTURA COMPLETA DE ATRIBUTOS:', datosCompletos.atributos);
      console.log('🔍 CLAVES DE ATRIBUTOS DISPONIBLES:', Object.keys(datosCompletos.atributos));

    } catch (error) {
      console.error('❌ Error general cargando datos:', error);
    } finally {
      setCargandoDatos(false);
    }
  };

  // Funciones de carga individual de cada API
  const cargarAtributos = async () => {
    const response = await atributoService.obtenerConfiguracionCompleta(selectedCompany.id);
    console.log('🔍 Response completa de atributos:', response);
    console.log('🔍 Estructura de datos:', response.data);
    return response.success ? (response.data.sub_atributos || {}) : {};
  };

  const cargarRegimenesLaborales = async () => {
    const response = await regimenLaboralService.getAll();
    return response.success ? (response.data || []) : [];
  };

  const cargarFormulas = async () => {
    const response = await formulaService.obtenerFormulas();
    return response.success && response.data ? 
      (Array.isArray(response.data.data) ? response.data.data : []) : [];
  };

  const cargarAfps = async () => {
    const response = await afpService.obtenerAfps();
    return response.success && response.data ? 
      (Array.isArray(response.data) ? response.data : []) : [];
  };

  const cargarInformacionGeneral = async () => {
    const response = await informacionGeneralService.obtenerInformacionActual();
    return response.success && response.data ? response.data : {};
  };

  const cargarTramosRenta = async () => {
    const response = await tramoRentaService.obtenerTramosRenta();
    return response.success && response.data ? 
      (Array.isArray(response.data) ? response.data : []) : [];
  };

  const cargarCodigosPlame = async () => {
    const response = await plameConfiguracionService.obtenerConfiguraciones({ per_page: 1000 });
    
    if (response.success) {
      const conceptosCompletos = response.data.data.map(config => ({
        id: config.id,
        codigo: config.codigo_plame,
        descripcion: config.descripcion,
        clasificacion: config.clasificacion,
        clasificacion_id: config.clasificacion?.id || config.clasificacion_id,
        id_tipo: config.clasificacion?.id_tipo || null,
        // Incluir todas las afectaciones
        essalud_regular: config.essalud_regular,
        essalud_cbssp_seg: config.essalud_cbssp_seg,
        essalud_regular_pension: config.essalud_regular_pension,
        essalud_sctr: config.essalud_sctr,
        fondo_compl_jubil: config.fondo_compl_jubil,
        fondo_derechos_artista: config.fondo_derechos_artista,
        impuesto_extraord_solid: config.impuesto_extraord_solid,
        reg_esp_pensiones_pesq: config.reg_esp_pensiones_pesq,
        renta_5ta_categoria: config.renta_5ta_categoria,
        senati: config.senati,
        sistema_nacional_19990: config.sistema_nacional_19990,
        sistema_privado_pension: config.sistema_privado_pension,
        contrib_solidaria_assist: config.contrib_solidaria_assist
      }));
      
      console.log('✅ Códigos PLAME cargados con afectaciones:', conceptosCompletos.length);
      console.log('📋 Conceptos PLAME completos con afectaciones:', conceptosCompletos);
      
      // Mostrar ejemplo de afectaciones
      if (conceptosCompletos.length > 0) {
        const conceptoEjemplo = conceptosCompletos[0];
        console.log('🔍 Ejemplo de concepto PLAME con afectaciones:', {
          codigo: conceptoEjemplo.codigo,
          descripcion: conceptoEjemplo.descripcion,
          clasificacion_id: conceptoEjemplo.clasificacion_id,
          afectaciones: {
            essalud_regular: conceptoEjemplo.essalud_regular,
            essalud_cbssp_seg: conceptoEjemplo.essalud_cbssp_seg,
            essalud_sctr: conceptoEjemplo.essalud_sctr,
            fondo_compl_jubil: conceptoEjemplo.fondo_compl_jubil,
            fondo_derechos_artista: conceptoEjemplo.fondo_derechos_artista,
            impuesto_extraord_solid: conceptoEjemplo.impuesto_extraord_solid,
            reg_esp_pensiones_pesq: conceptoEjemplo.reg_esp_pensiones_pesq,
            renta_5ta_categoria: conceptoEjemplo.renta_5ta_categoria,
            senati: conceptoEjemplo.senati,
            sistema_nacional_19990: conceptoEjemplo.sistema_nacional_19990,
            sistema_privado_pension: conceptoEjemplo.sistema_privado_pension,
            contrib_solidaria_assist: conceptoEjemplo.contrib_solidaria_assist
          }
        });
      }
      
      return conceptosCompletos;
    }
    
    return [];
  };

  const cargarPlanillaAlternativa = async () => {
    try {
      const response = await configuracionPlanillaAlternativaService.verificarEstado(selectedCompany.id);
      return Boolean(response?.data?.planilla_alternativa_activa);
    } catch (error) {
      console.error('❌ Error cargando planilla alternativa:', error);
      return false;
    }
  };

  const cargarViaticosStandard = async () => {
    try {
      const response = await configuracionViaticosStandardService.verificarEstado(selectedCompany.id);
      return Boolean(response?.data?.viaticos_standard_activo ?? response?.data?.activo);
    } catch (error) {
      console.error('❌ Error cargando viáticos standard:', error);
      return false;
    }
  };

  // Función para manejar la selección de archivo
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setArchivo(file);
      procesarArchivo(file);
    }
  };

  // Función para limpiar headers (remover asteriscos y espacios extra)
  const limpiarHeaders = (datos) => {
    return datos.map(fila => {
      const filaLimpia = {};
      Object.keys(fila).forEach(header => {
        // Remover asterisco y espacios extra del header
        const headerLimpio = header.replace(/\s*\*\s*$/, '').trim();
        filaLimpia[headerLimpio] = fila[header];
      });
      return filaLimpia;
    });
  };

  // Función para procesar el archivo Excel
  const procesarArchivo = async (file) => {
    setProcesandoArchivo(true);
    setDatosExcel([]);
    setConceptosExcel([]);
    setValidaciones([]);
    setValidacionesConceptos([]);
    setErroresValidacion([]);
    setErroresValidacionConceptos([]);
    
    try {
      console.log('📄 Procesando archivo:', file.name);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const workbook = XLSX.read(e.target.result, { type: 'binary', cellDates: true });
          const sheetNames = workbook.SheetNames || [];
          
          console.log('📋 Hojas disponibles:', sheetNames);
          
          const hojaEmpleados =
            buscarHoja(sheetNames, ['empleados', 'empleado']) || sheetNames[0];
          if (!hojaEmpleados) {
            setErroresValidacion([{
              fila: 0,
              error: 'El archivo debe contener la hoja "Empleados"',
            }]);
            return;
          }

          const worksheetEmpleados = workbook.Sheets[hojaEmpleados];
          const datosEmpleados = limpiarHeaders(XLSX.utils.sheet_to_json(worksheetEmpleados)).map(
            (fila) => {
              const filaNormalizada = { ...fila };
              CAMPOS_FECHA_EMPLEADO.forEach((campo) => {
                if (campo in filaNormalizada) {
                  filaNormalizada[campo] = normalizarFechaExcel(filaNormalizada[campo]);
                }
              });
              return filaNormalizada;
            }
          );

          // Conceptos: con PA → Conceptos_PLAME + Conceptos_Interna (o Conceptos con tipo_boleta)
          let datosConceptos = [];
          const hojaPlame = buscarHoja(sheetNames, [
            'conceptos_plame',
            'conceptos plame',
            'concepto_plame',
          ]);
          const hojaInterna = buscarHoja(sheetNames, [
            'conceptos_interna',
            'conceptos internas',
            'conceptos_interno',
            'conceptos interna',
            'concepto_interna',
          ]);
          const hojaConceptos = buscarHoja(sheetNames, ['conceptos', 'concepto']);

          const leerHojaConceptos = (nombreHoja, tipoForzado = null) => {
            if (!nombreHoja || !workbook.Sheets[nombreHoja]) return [];
            const rows = limpiarHeaders(XLSX.utils.sheet_to_json(workbook.Sheets[nombreHoja]));
            return rows.map((fila) => ({
              ...fila,
              tipo_boleta:
                tipoForzado ||
                normalizarTipoBoleta(fila.tipo_boleta) ||
                (planillaAlternativaActiva ? null : 'plame'),
            }));
          };

          if (planillaAlternativaActiva && (hojaPlame || hojaInterna)) {
            datosConceptos = [
              ...leerHojaConceptos(hojaPlame, 'plame'),
              ...leerHojaConceptos(hojaInterna, 'interna'),
            ];
          } else if (hojaConceptos) {
            datosConceptos = leerHojaConceptos(hojaConceptos, null).map((fila) => ({
              ...fila,
              tipo_boleta:
                normalizarTipoBoleta(fila.tipo_boleta) ||
                (planillaAlternativaActiva ? null : 'plame'),
            }));
          } else if (sheetNames.length >= 2) {
            // Compatibilidad: segunda hoja = conceptos
            datosConceptos = leerHojaConceptos(sheetNames[1], planillaAlternativaActiva ? null : 'plame');
          } else {
            setErroresValidacion([{
              fila: 0,
              error: planillaAlternativaActiva
                ? 'Faltan hojas de conceptos. Use "Conceptos_PLAME" y/o "Conceptos_Interna" (o "Conceptos" con columna tipo_boleta).'
                : 'El archivo debe contener las hojas "Empleados" y "Conceptos"',
            }]);
            return;
          }
          
          console.log('📊 Empleados:', datosEmpleados.length, '| Conceptos:', datosConceptos.length, {
            hojaPlame,
            hojaInterna,
            hojaConceptos,
          });
          
          setDatosExcel(datosEmpleados);
          setConceptosExcel(datosConceptos);
          
          validarDatos(datosEmpleados);
          validarConceptos(datosConceptos, datosEmpleados);
          
        } catch (error) {
          console.error('❌ Error procesando archivo Excel:', error);
          setErroresValidacion([{ 
            fila: 0, 
            error: 'Error al procesar el archivo Excel. Verifique que sea un archivo válido.' 
          }]);
        }
      };
      
      reader.readAsBinaryString(file);
      
    } catch (error) {
      console.error('❌ Error leyendo archivo:', error);
      setErroresValidacion([{ 
        fila: 0, 
        error: 'Error al leer el archivo. Verifique que sea un archivo Excel válido.' 
      }]);
    } finally {
      setProcesandoArchivo(false);
    }
  };

  // Función para validar los datos del Excel
  const validarDatos = (datos) => {
    console.log('🔍 Iniciando validación de datos...');
    console.log('🔍 Atributos disponibles para validación:', datosApis.atributos);
    console.log('🔍 Claves disponibles:', Object.keys(datosApis.atributos));
    
    const errores = [];
    const empleadosValidos = [];
    const validacionesDetalle = [];

    datos.forEach((fila, index) => {
      const numeroFila = index + 2; // +2 porque Excel empieza en 1 y tiene header
      const erroresFila = [];
      
      // Validaciones básicas
      if (!fila.nombre_completo || fila.nombre_completo.trim() === '') {
        erroresFila.push('Nombre completo es obligatorio');
      }
      
      if (!fila.tipo_documento || fila.tipo_documento.trim() === '') {
        erroresFila.push('Tipo de documento es obligatorio');
      } else if (
        !['DNI', 'Carnet de Extranjería', 'Pasaporte'].includes(String(fila.tipo_documento).trim())
      ) {
        erroresFila.push('Tipo de documento debe ser DNI, Carnet de Extranjería o Pasaporte');
      }
      
      if (!fila.numero_documento || fila.numero_documento.toString().trim() === '') {
        erroresFila.push('Número de documento es obligatorio');
      } else if (fila.tipo_documento === 'DNI' && fila.numero_documento.toString().trim().length !== 8) {
        erroresFila.push('DNI debe tener exactamente 8 dígitos');
      } else if (
        fila.tipo_documento === 'Carnet de Extranjería' &&
        !/^[A-Za-z0-9]{6,20}$/.test(fila.numero_documento.toString().trim())
      ) {
        erroresFila.push('Carnet de Extranjería debe tener entre 6 y 20 caracteres alfanuméricos');
      }
      
      if (!fila.fecha_nacimiento || fila.fecha_nacimiento.toString().trim() === '') {
        erroresFila.push('Fecha de nacimiento es obligatoria y debe ser una fecha válida (aaaa-mm-dd)');
      }
      
      if (!fila.sexo || fila.sexo.trim() === '') {
        erroresFila.push('Sexo es obligatorio');
      } else if (!['Masculino', 'Femenino'].includes(fila.sexo)) {
        erroresFila.push('Sexo debe ser "Masculino" o "Femenino"');
      }
      
      if (!fila.cargo || fila.cargo.trim() === '') {
        erroresFila.push('Cargo es obligatorio');
      } else {
        // Validar que el cargo exista en los atributos
        const cargosDisponibles = datosApis.atributos.Cargos || [];
        const cargoExiste = cargosDisponibles.some(c => c.nombre === fila.cargo);
        if (!cargoExiste) {
          erroresFila.push(`Cargo "${fila.cargo}" no existe en el sistema`);
        }
      }
      
      if (!fila.categoria || fila.categoria.trim() === '') {
        erroresFila.push('Categoría es obligatoria');
      } else {
        // Validar que la categoría exista
        const categoriasDisponibles = datosApis.atributos.Categorías || [];
        const categoriaExiste = categoriasDisponibles.some(c => c.nombre === fila.categoria);
        if (!categoriaExiste) {
          erroresFila.push(`Categoría "${fila.categoria}" no existe en el sistema`);
        }
      }
      
      if (!fila.area_trabajo || fila.area_trabajo.trim() === '') {
        erroresFila.push('Área de trabajo es obligatoria');
      } else {
        // Validar que el área de trabajo exista
        const areasDisponibles =
          datosApis.atributos['Áreas'] ||
          datosApis.atributos['Area de Trabajo'] ||
          datosApis.atributos['Área de Trabajo'] ||
          [];
        console.log(`🔍 Validando área trabajo para "${fila.nombre_completo}":`, {
          valorExcel: fila.area_trabajo,
          areasDisponibles: areasDisponibles,
          nombresDisponibles: areasDisponibles.map(a => a.nombre)
        });
        const areaExiste = areasDisponibles.some(a => a.nombre === fila.area_trabajo);
        if (!areaExiste) {
          erroresFila.push(`Área de trabajo "${fila.area_trabajo}" no existe en el sistema`);
        }
      }
      
      if (!fila.proyecto || fila.proyecto.trim() === '') {
        erroresFila.push('Proyecto es obligatorio');
      } else {
        // Validar que el proyecto exista
        const proyectosDisponibles = datosApis.atributos.Proyectos || [];
        console.log(`🔍 Validando proyecto para "${fila.nombre_completo}":`, {
          valorExcel: fila.proyecto,
          proyectosDisponibles: proyectosDisponibles,
          nombresDisponibles: proyectosDisponibles.map(p => p.nombre)
        });
        const proyectoExiste = proyectosDisponibles.some(p => p.nombre === fila.proyecto);
        if (!proyectoExiste) {
          erroresFila.push(`Proyecto "${fila.proyecto}" no existe en el sistema`);
        }
      }
      
      if (!fila.tipo_contratacion || fila.tipo_contratacion.trim() === '') {
        erroresFila.push('Tipo de contratación es obligatorio');
      } else {
        if (!esTipoContratacionEmpleadoValido(fila.tipo_contratacion, planillaAlternativaActiva)) {
          const tiposOk = obtenerTiposContratacionEmpleado(planillaAlternativaActiva).join(', ');
          erroresFila.push(
            `Tipo de contratación "${fila.tipo_contratacion}" no válido. Use: ${tiposOk}`
          );
        }
      }
      
      if (!fila.estado || fila.estado.trim() === '') {
        erroresFila.push('Estado es obligatorio');
      } else if (!['Activo', 'Inactivo'].includes(fila.estado)) {
        erroresFila.push('Estado debe ser "Activo" o "Inactivo"');
      }
      
      if (!fila.regimen_laboral || fila.regimen_laboral.trim() === '') {
        erroresFila.push('Régimen laboral es obligatorio');
      } else {
        const regimenExiste = datosApis.regimenesLaborales.some(r => r.nombre === fila.regimen_laboral);
        if (!regimenExiste) {
          erroresFila.push(`Régimen laboral "${fila.regimen_laboral}" no existe en el sistema`);
        }
      }
      
      // Validar planilla alternativa (dual / solo_interna) o flujo clásico
      let tipoPlanillaNormalizado = null;
      if (planillaAlternativaActiva) {
        const esContratacionInterna = String(fila.tipo_contratacion || '').trim() === 'Interno';
        tipoPlanillaNormalizado = esContratacionInterna
          ? 'solo_interna'
          : normalizarTipoPlanilla(fila.tipo_planilla);
        if (!tipoPlanillaNormalizado) {
          erroresFila.push('tipo_planilla es obligatorio: use "dual" o "solo_interna"');
        } else {
          if (!fila.sueldo_interno || parseFloat(fila.sueldo_interno) <= 0) {
            erroresFila.push('sueldo_interno (control) debe ser mayor a 0');
          }
          if (!fila.fecha_ingreso_interna || fila.fecha_ingreso_interna.toString().trim() === '') {
            erroresFila.push(
              'fecha_ingreso_interna es obligatoria con planilla alternativa y debe ser una fecha válida (aaaa-mm-dd)'
            );
          }

          if (tipoPlanillaNormalizado === 'dual') {
            if (!fila.sueldo_basico || parseFloat(fila.sueldo_basico) <= 0) {
              erroresFila.push('sueldo_basico (PLAME) debe ser mayor a 0 en modo dual');
            }
            if (!fila.fecha_ingreso || fila.fecha_ingreso.toString().trim() === '') {
              erroresFila.push(
                'fecha_ingreso (PLAME) es obligatoria en modo dual y debe ser una fecha válida (aaaa-mm-dd)'
              );
            }
            if (!fila.sistema_pensionario || fila.sistema_pensionario.trim() === '') {
              erroresFila.push('Sistema pensionario es obligatorio en modo dual');
            } else if (!['AFP', 'ONP'].includes(fila.sistema_pensionario)) {
              erroresFila.push('Sistema pensionario debe ser "AFP" o "ONP"');
            }
          }
          // solo_interna: no exige sueldo_basico PLAME ni sistema pensionario
        }
      } else {
        if (!fila.sueldo_basico || parseFloat(fila.sueldo_basico) <= 0) {
          erroresFila.push('Sueldo básico debe ser mayor a 0');
        }
        if (!fila.sistema_pensionario || fila.sistema_pensionario.trim() === '') {
          erroresFila.push('Sistema pensionario es obligatorio');
        } else if (!['AFP', 'ONP'].includes(fila.sistema_pensionario)) {
          erroresFila.push('Sistema pensionario debe ser "AFP" o "ONP"');
        }
        if (!fila.fecha_ingreso || fila.fecha_ingreso.toString().trim() === '') {
          erroresFila.push('Fecha de ingreso es obligatoria');
        }
      }
      
      if (!fila.estado_civil || fila.estado_civil.trim() === '') {
        erroresFila.push('Estado civil es obligatorio');
      } else if (!['Soltero', 'Casado', 'Divorciado', 'Viudo', 'Conviviente'].includes(fila.estado_civil)) {
        erroresFila.push('Estado civil debe ser: Soltero, Casado, Divorciado, Viudo o Conviviente');
      }
      
      if (fila.numero_hijos === undefined || fila.numero_hijos === null || fila.numero_hijos === '') {
        erroresFila.push('Número de hijos es obligatorio');
      } else if (isNaN(parseInt(fila.numero_hijos)) || parseInt(fila.numero_hijos) < 0) {
        erroresFila.push('Número de hijos debe ser un número mayor o igual a 0');
      }
      
      // Validar teléfono (opcional, pero si se proporciona debe ser válido)
      if (fila.telefono && fila.telefono.toString().trim() !== '') {
        if (fila.telefono.toString().length < 7 || fila.telefono.toString().length > 15) {
          erroresFila.push('Teléfono debe tener entre 7 y 15 dígitos');
        }
      }
      
      // Validar email (opcional, pero si se proporciona debe tener formato válido)
      if (fila.email && fila.email.trim() !== '') {
        if (!/\S+@\S+\.\S+/.test(fila.email)) {
          erroresFila.push('Email no tiene formato válido');
        }
      }
      
      if (!fila.direccion || fila.direccion.trim() === '') {
        erroresFila.push('Dirección es obligatoria');
      }
      
      if (!fila.distrito || fila.distrito.trim() === '') {
        erroresFila.push('Distrito es obligatorio');
      }
      
      if (!fila.provincia || fila.provincia.trim() === '') {
        erroresFila.push('Provincia es obligatoria');
      }
      
      if (!fila.departamento || fila.departamento.trim() === '') {
        erroresFila.push('Departamento es obligatorio');
      }
      
      // Validar campos AFP solo si sistema pensionario es AFP (no aplica a solo_interna sin pensión)
      const esSoloInternaFila =
        planillaAlternativaActiva && resolverTipoPlanilla(fila) === 'solo_interna';
      if (!esSoloInternaFila && fila.sistema_pensionario === 'AFP') {
        // Validar AFP (obligatorio cuando es AFP)
        if (!fila.afp || fila.afp.trim() === '') {
          erroresFila.push('AFP es obligatoria cuando el sistema pensionario es AFP');
        } else {
          const afpExiste = datosApis.afps.some(a => a.nombre === fila.afp);
          if (!afpExiste) {
            erroresFila.push(`AFP "${fila.afp}" no existe en el sistema`);
          }
        }
        
        // Validar tipo AFP (obligatorio cuando es AFP)
        if (!fila.tipo_afp || fila.tipo_afp.trim() === '') {
          erroresFila.push('Tipo de AFP es obligatorio cuando el sistema pensionario es AFP');
        } else if (!['Flujo', 'Mixto', 'Balance'].includes(fila.tipo_afp)) {
          erroresFila.push('Tipo de AFP debe ser "Flujo", "Mixto" o "Balance"');
        }
        
        // Validar CUSPP (obligatorio cuando es AFP)
        const cusppNormalizado = normalizarCuspp(fila.cuspp);
        if (!cusppNormalizado) {
          erroresFila.push('CUSPP es obligatorio cuando el sistema pensionario es AFP');
        } else if (cusppNormalizado.length > 12) {
          erroresFila.push(
            `CUSPP debe tener máximo 12 caracteres alfanuméricos; "${cusppNormalizado}" tiene ${cusppNormalizado.length}`
          );
        }
      } else {
        // Si es ONP, validar que los campos AFP sean válidos si se proporcionan (pero no son obligatorios)
        if (fila.afp && fila.afp.trim() !== '') {
          const afpExiste = datosApis.afps.some(a => a.nombre === fila.afp);
          if (!afpExiste) {
            erroresFila.push(`AFP "${fila.afp}" no existe en el sistema (campo se ignorará porque es sistema ONP)`);
          }
        }
        if (fila.tipo_afp && fila.tipo_afp.trim() !== '' && !['Flujo', 'Mixto', 'Balance'].includes(fila.tipo_afp)) {
          erroresFila.push('Tipo de AFP debe ser "Flujo", "Mixto" o "Balance" (campo se ignorará porque es sistema ONP)');
        }
        const cusppOnp = normalizarCuspp(fila.cuspp);
        if (cusppOnp && cusppOnp.length > 12) {
          erroresFila.push(
            `CUSPP debe tener máximo 12 caracteres alfanuméricos; "${cusppOnp}" tiene ${cusppOnp.length} (campo se ignorará porque es sistema ONP)`
          );
        }
      }
      
      // Validar hora de entrada (obligatorio)
      if (!fila.hora_entrada || fila.hora_entrada.toString().trim() === '') {
        erroresFila.push('Hora de entrada es obligatoria');
      } else {
        // Validar formato HH:MM
        const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
        if (!timeRegex.test(fila.hora_entrada.toString())) {
          erroresFila.push('Hora de entrada debe tener formato HH:MM (ej: 08:00)');
        }
      }
      
      // Agregar errores o empleado válido
      if (erroresFila.length > 0) {
        errores.push({
          fila: numeroFila,
          empleado: fila.nombre_completo || `Fila ${numeroFila}`,
          errores: erroresFila
        });
      } else {
        empleadosValidos.push({
          ...fila,
          fila: numeroFila,
          // Normalizar tipo para el resto del flujo de importación
          tipo_planilla: planillaAlternativaActiva ? resolverTipoPlanilla(fila) : null,
        });
      }
      
      // Agregar detalle de validación
      validacionesDetalle.push({
        fila: numeroFila,
        empleado: fila.nombre_completo || `Fila ${numeroFila}`,
        estado: erroresFila.length > 0 ? 'error' : 'valido',
        errores: erroresFila
      });
    });

    setErroresValidacion(errores);
    setEmpleadosValidos(empleadosValidos);
    setValidaciones(validacionesDetalle);
    setMostrarValidaciones(true);
    
    console.log('📊 Resultado de validaciones empleados:', {
      totalFilas: datos.length,
      empleadosValidos: empleadosValidos.length,
      empleadosConErrores: errores.length
    });
  };

  // Función para validar los conceptos del Excel
  const validarConceptos = (conceptos, datosEmpleados = null) => {
    console.log('🔍 Iniciando validación de conceptos...');
    console.log('🔍 Conceptos:', conceptos);
    console.log('🔍 Datos empleados recibidos:', datosEmpleados);
    
    const errores = [];
    const conceptosValidosTemp = [];
    const validacionesDetalle = [];
    const empleadosParaValidar = datosEmpleados || datosExcel;

    conceptos.forEach((fila, index) => {
      const numeroFila = index + 2;
      const erroresFila = [];

      const tipoBoleta =
        normalizarTipoBoleta(fila.tipo_boleta) ||
        (!planillaAlternativaActiva ? 'plame' : null);

      const documento = obtenerDocumentoConcepto(fila);
      
      if (!documento) {
        erroresFila.push('Número de documento es obligatorio (DNI o Carnet de Extranjería)');
      } else if (!esDocumentoConceptoValido(documento)) {
        erroresFila.push(
          'Documento inválido: use DNI (8 dígitos) o Carnet de Extranjería (6–20 caracteres)'
        );
      }
      
      if (!fila.codigo_plame || fila.codigo_plame.toString().trim() === '') {
        erroresFila.push('Código PLAME es obligatorio');
      } else {
        const codigo = fila.codigo_plame.toString().trim();
        const conceptoEncontrado = datosApis.conceptosPlame.find(
          (concepto) => concepto.codigo === codigo
        );
        if (!conceptoEncontrado) {
          erroresFila.push(`Código PLAME "${codigo}" no existe en el sistema`);
        }
      }
      
      if (!fila.monto || isNaN(parseFloat(fila.monto)) || parseFloat(fila.monto) <= 0) {
        erroresFila.push('Monto debe ser un número mayor a 0');
      }

      if (planillaAlternativaActiva && !tipoBoleta) {
        erroresFila.push('tipo_boleta es obligatorio: use "plame" o "interna" (o hojas Conceptos_PLAME / Conceptos_Interna)');
      }
      
      const empleadoExcel = empleadosParaValidar.find(
        (emp) => emp.numero_documento?.toString().trim() === documento
      );
      if (!empleadoExcel && documento) {
        erroresFila.push(
          `No se encontró empleado con documento "${documento}" en la hoja de empleados (DNI o Carnet de Extranjería)`
        );
      } else if (empleadoExcel && planillaAlternativaActiva && tipoBoleta) {
        const tipoEmp = resolverTipoPlanilla(empleadoExcel);
        if (tipoEmp === 'solo_interna' && tipoBoleta === 'plame') {
          erroresFila.push(
            'Empleado "solo_interna": no use conceptos PLAME; colóquelos en Conceptos_Interna'
          );
        }
      }
      
      if (erroresFila.length > 0) {
        errores.push({
          fila: numeroFila,
          dni: documento || `Fila ${numeroFila}`,
          concepto: fila.codigo_plame || 'Sin código',
          errores: erroresFila
        });
      } else {
        conceptosValidosTemp.push({
          ...fila,
          fila: numeroFila,
          DNI: documento,
          numero_documento: documento,
          codigo_plame: fila.codigo_plame.toString().trim(),
          monto: parseFloat(fila.monto),
          tipo_boleta: tipoBoleta || 'plame',
        });
      }
      
      validacionesDetalle.push({
        fila: numeroFila,
        dni: documento || `Fila ${numeroFila}`,
        concepto: fila.codigo_plame || 'Sin código',
        estado: erroresFila.length > 0 ? 'error' : 'valido',
        errores: erroresFila
      });
    });

    setErroresValidacionConceptos(errores);
    setConceptosValidos(conceptosValidosTemp);
    setValidacionesConceptos(validacionesDetalle);
    
    console.log('📊 Resultado de validaciones conceptos:', {
      totalFilas: conceptos.length,
      conceptosValidos: conceptosValidosTemp.length,
      conceptosConErrores: errores.length,
      plame: conceptosValidosTemp.filter((c) => c.tipo_boleta === 'plame').length,
      interna: conceptosValidosTemp.filter((c) => c.tipo_boleta === 'interna').length,
    });
  };

  // Función para calcular conceptos automáticamente usando el service de cálculo de boletas
  // Dual: PLAME (con ONP/AFP/EsSalud) → se persisten conceptos PLAME.
  // Solo interna: solo ingresos de control, sin descuentos ni aportes.
  const calcularConceptosAutomaticos = async (empleadoData) => {
    try {
      console.log(`🧮 Calculando conceptos automáticos para: ${empleadoData.nombre_completo}`);

      const tipoPlanilla = planillaAlternativaActiva
        ? resolverTipoPlanilla(empleadoData)
        : null;
      const esSoloInterna = tipoPlanilla === 'solo_interna';
      const esDual = tipoPlanilla === 'dual';

      const sueldoPlame = parseFloat(empleadoData.sueldo_basico) || 0;
      const sueldoInterno = parseFloat(empleadoData.sueldo_interno) || 0;
      
      // Conceptos del Excel separados por boleta (PLAME vs interna)
      const conceptosEmpleadoExcel = conceptosValidos.filter(concepto => 
        (concepto.DNI || concepto.numero_documento) === empleadoData.numero_documento?.toString().trim()
      );
      const conceptosExcelPlame = conceptosEmpleadoExcel.filter(
        (c) => (c.tipo_boleta || 'plame') === 'plame'
      );
      const conceptosExcelInterna = conceptosEmpleadoExcel.filter(
        (c) => c.tipo_boleta === 'interna'
      );

      const mapearExcelAIngresos = (lista) =>
        lista.map((concepto) => {
          const conceptoPlame = datosApis.conceptosPlame.find(
            (cp) => cp.codigo === concepto.codigo_plame
          );
          return {
            codigoPlame: concepto.codigo_plame,
            concepto: conceptoPlame?.descripcion || 'Concepto del Excel',
            id: conceptoPlame?.id || null,
            monto: concepto.monto,
            plame_configuracion_id: conceptoPlame?.id || null,
            tipo_concepto: 'ingreso',
          };
        });

      const ingresosDelExcelPlame = mapearExcelAIngresos(
        esSoloInterna ? [] : conceptosExcelPlame
      );
      const ingresosDelExcelInterna = mapearExcelAIngresos(
        esSoloInterna || esDual ? conceptosExcelInterna : []
      );

      console.log(`📋 Conceptos Excel / tipo_planilla=${tipoPlanilla}:`, {
        plame: ingresosDelExcelPlame,
        interna: ingresosDelExcelInterna,
      });
      
      const datosCompletos = {
        datosEmpresa: {
          id: selectedCompany.id,
          nombre: selectedCompany.nombre || 'Empresa Seleccionada'
        },
        datosAPIs: {
          regimenesLaborales: datosApis.regimenesLaborales,
          regimenSeleccionado: datosApis.regimenesLaborales.find(r => r.nombre === empleadoData.regimen_laboral),
          conceptosPlame: datosApis.conceptosPlame,
          conceptosIngresos: datosApis.conceptosPlame.filter(c => c.id_tipo === 1),
          conceptosDescuentos: datosApis.conceptosPlame.filter(c => c.id_tipo === 2),
          conceptosAportes: datosApis.conceptosPlame.filter(c => c.id_tipo === 3),
          formulas: datosApis.formulas,
          formulasIngresos: Array.isArray(datosApis.formulas) ? datosApis.formulas.filter(f => f.id_tipo === 1) : [],
          formulasDescuentos: Array.isArray(datosApis.formulas) ? datosApis.formulas.filter(f => f.id_tipo === 2) : [],
          formulasAportes: Array.isArray(datosApis.formulas) ? datosApis.formulas.filter(f => f.id_tipo === 3) : [],
          afps: datosApis.afps,
          uit: datosApis.informacionGeneral.uit || null,
          sueldoMinimo: datosApis.informacionGeneral.sueldo_minimo || null,
          informacionGeneral: datosApis.informacionGeneral,
          tramosRenta: datosApis.tramosRenta,
          atributosData: datosApis.atributos
        },
        datosSalariales: {
          sueldoBasico: esSoloInterna ? sueldoInterno : sueldoPlame,
          sueldoInterno: planillaAlternativaActiva ? sueldoInterno : 0,
          tipoPlanilla,
          sistemaPensionario: empleadoData.sistema_pensionario || 'ONP',
          afp: empleadoData.afp || null,
          tipoAfp: empleadoData.tipo_afp || 'Flujo',
          cuspp: normalizarCuspp(empleadoData.cuspp) || null,
          regimenLaboral: empleadoData.regimen_laboral,
          tipoContratacion: empleadoData.tipo_contratacion
        },
        conceptosUsuario: {
          ingresos: ingresosDelExcelPlame,
          descuentos: [],
          aportes: [],
          ingresosInternos: ingresosDelExcelInterna,
        },
        datosEmpleado: {
          nombre_completo: empleadoData.nombre_completo,
          numero_documento: empleadoData.numero_documento,
          fecha_nacimiento: empleadoData.fecha_nacimiento,
          sexo: empleadoData.sexo,
          estado_civil: empleadoData.estado_civil,
          numero_hijos: parseInt(empleadoData.numero_hijos) || 0,
          email: empleadoData.email,
          telefono: empleadoData.telefono,
          fecha_ingreso: empleadoData.fecha_ingreso
        }
      };

      const mapearResultadoAConceptos = (
        resultado,
        incluirDescAportes = true,
        tipoBoleta = 'plame'
      ) => {
        const conceptosCalculados = [];
        if (!resultado?.success) return conceptosCalculados;
        const { dataIngresos, dataDescuentos, dataAportes } = resultado.data || {};

        const agregar = (lista, tipoConcepto) => {
          if (!Array.isArray(lista)) return;
          lista.forEach((item) => {
            const id = item?.id_concepto?.id;
            // Los conceptos sintéticos (id no numérico) no existen en plame_configuracion
            if (!Number.isInteger(Number(id)) || Number(item.valor) <= 0) return;
            conceptosCalculados.push({
              plame_configuracion_id: Number(id),
              tipo_concepto: tipoConcepto,
              tipo_boleta: tipoBoleta,
              monto: parseFloat(item.valor),
            });
          });
        };

        agregar(dataIngresos, 'ingreso');
        if (incluirDescAportes) {
          agregar(dataDescuentos, 'descuento');
          agregar(dataAportes, 'aporte');
        }

        return conceptosCalculados;
      };

      // solo_interna: solo motor interno (sin ONP/AFP ni aportes)
      if (esSoloInterna) {
        const resultadoInterna = await calculosBoletaInternaService.generarCalculos(
          {
            ...datosCompletos,
            datosSalariales: {
              ...datosCompletos.datosSalariales,
              sueldoInterno,
              sueldoBasico: sueldoInterno,
            },
          },
          null,
          'importacion_empleados_excel_interna'
        );
        // solo_interna: la boleta de control es la única, se guarda como boleta principal
        const conceptos = mapearResultadoAConceptos(resultadoInterna, false, 'plame');
        console.log(`💰 Solo interna — conceptos para ${empleadoData.nombre_completo}:`, conceptos);
        return conceptos;
      }

      // dual o sin PA: motor PLAME (oficial)
      const resultadoPlame = await calculosBoletaService.generarCalculos(
        datosCompletos,
        'importacion_empleados_excel'
      );
      console.log('✅ Resultados PLAME:', resultadoPlame);

      // dual: correr interna post-PLAME (ingresos control; desc/aportes = copia PLAME)
      let conceptosInternos = [];
      if (esDual) {
        const resultadoInterna = await calculosBoletaInternaService.generarCalculos(
          {
            ...datosCompletos,
            datosSalariales: {
              ...datosCompletos.datosSalariales,
              sueldoInterno,
            },
          },
          resultadoPlame,
          'importacion_empleados_excel_interna'
        );
        conceptosInternos = mapearResultadoAConceptos(resultadoInterna, true, 'interna');
      }

      const conceptosCalculados = [
        ...mapearResultadoAConceptos(resultadoPlame, true, 'plame'),
        ...conceptosInternos,
      ];
      console.log(`💰 Conceptos finales para ${empleadoData.nombre_completo}:`, {
        total: conceptosCalculados.length,
        internos: conceptosInternos.length,
        conceptosDelExcel: conceptosEmpleadoExcel.length,
        dual: esDual,
      });
      return conceptosCalculados;
      
    } catch (error) {
      console.error(`❌ Error calculando conceptos para ${empleadoData.nombre_completo}:`, error);
      return [];
    }
  };

  // Función para importar empleados válidos
  const importarEmpleados = async () => {
    if (empleadosValidos.length === 0) return;
    
    setImportandoEmpleados(true);
    const resultados = {
      exitosos: [],
      fallidos: []
    };

    try {
      console.log('🚀 Iniciando importación REAL de empleados válidos en base de datos...');
      
      for (const empleadoData of empleadosValidos) {
        try {
          console.log(`📤 Procesando empleado: ${empleadoData.nombre_completo}`);
          
          // Calcular conceptos automáticamente usando el service de cálculo de boletas
          // NOTA: Los conceptos del Excel ya están incluidos en conceptosCalculados
          const conceptosCalculados = await calcularConceptosAutomaticos(empleadoData);
          
          // Los conceptos para el backend son solo los calculados (que ya incluyen los del Excel)
          const conceptosParaBackend = conceptosCalculados;
          
          // Desglose detallado de conceptos por tipo (incluye conceptos calculados + conceptos del Excel)
          const conceptosPlameBackend = conceptosCalculados.filter(
            (c) => (c.tipo_boleta || 'plame') === 'plame'
          );
          const conteoConceptos = {
            ingresos: conceptosPlameBackend.filter(c => c.tipo_concepto === 'ingreso').length,
            descuentos: conceptosPlameBackend.filter(c => c.tipo_concepto === 'descuento').length,
            aportes: conceptosPlameBackend.filter(c => c.tipo_concepto === 'aporte').length,
            internos: conceptosCalculados.filter(c => c.tipo_boleta === 'interna').length
          };
          
          // Obtener la cantidad de conceptos del Excel para este empleado (solo para reporte)
          const conceptosEmpleadoExcel = conceptosValidos.filter(concepto => 
            (concepto.DNI || concepto.numero_documento) === empleadoData.numero_documento?.toString().trim()
          );
          
          console.log(`💰 Total conceptos para ${empleadoData.nombre_completo}:`, {
            conceptos: {
              total: conceptosCalculados.length,
              desglose: conteoConceptos
            },
            conceptosDelExcel: conceptosEmpleadoExcel.length,
            nota: 'Los conceptos del Excel ya están incluidos en el total (no se duplican)'
          });

          // Preparar datos del empleado para el backend (incluye dual / solo_interna)
          const tipoContratacionImport = empleadoData.tipo_contratacion
            ? empleadoData.tipo_contratacion.toString().trim()
            : null;
          const tipoPlanilla = planillaAlternativaActiva
            ? resolverTipoPlanilla(empleadoData)
            : null;
          const esSoloInterna = tipoPlanilla === 'solo_interna';
          const sueldoInterno = planillaAlternativaActiva
            ? (parseFloat(empleadoData.sueldo_interno) || null)
            : null;
          const sueldoBasicoPayload = esSoloInterna
            ? (sueldoInterno || 0)
            : (parseFloat(empleadoData.sueldo_basico) || 0);
          const fechaIngresoInterna = planillaAlternativaActiva
            ? (empleadoData.fecha_ingreso_interna || null)
            : null;
          const fechaIngresoPayload = esSoloInterna
            ? (fechaIngresoInterna || empleadoData.fecha_ingreso || new Date().toISOString().split('T')[0])
            : (empleadoData.fecha_ingreso || new Date().toISOString().split('T')[0]);
          const sistemaPensionarioPayload = esSoloInterna
            ? (empleadoData.sistema_pensionario || 'ONP')
            : (empleadoData.sistema_pensionario || 'ONP');

          const empleadoParaBackend = {
            empresa_id: selectedCompany.id,
            nombre_completo: empleadoData.nombre_completo ? empleadoData.nombre_completo.toString().trim() : null,
            tipo_documento: empleadoData.tipo_documento || 'DNI',
            numero_documento: empleadoData.numero_documento ? empleadoData.numero_documento.toString().trim() : null,
            fecha_nacimiento: empleadoData.fecha_nacimiento || null,
            sexo: empleadoData.sexo ? empleadoData.sexo.toString().trim() : null,
            estado_civil: empleadoData.estado_civil ? empleadoData.estado_civil.toString().trim() : null,
            numero_hijos: parseInt(empleadoData.numero_hijos) || 0,
            telefono: empleadoData.telefono ? empleadoData.telefono.toString().trim() : null,
            email: empleadoData.email ? empleadoData.email.toString().trim() : null,
            direccion: empleadoData.direccion ? empleadoData.direccion.toString().trim() : null,
            distrito: empleadoData.distrito ? empleadoData.distrito.toString().trim() : null,
            provincia: empleadoData.provincia ? empleadoData.provincia.toString().trim() : null,
            departamento: empleadoData.departamento ? empleadoData.departamento.toString().trim() : null,
            cargo: empleadoData.cargo ? empleadoData.cargo.toString().trim() : null,
            categoria: empleadoData.categoria ? empleadoData.categoria.toString().trim() : null,
            areas_de_trabajo: empleadoData.area_trabajo ? empleadoData.area_trabajo.toString().trim() : null,
            proyecto: empleadoData.proyecto ? empleadoData.proyecto.toString().trim() : null,
            tipo_contratacion: tipoContratacionImport,
            estado: empleadoData.estado || 'Activo',
            regimen_laboral_id: obtenerIdRegimenLaboral(empleadoData.regimen_laboral),
            sueldo_basico: sueldoBasicoPayload,
            sueldo_interno: sueldoInterno,
            tipo_planilla: tipoPlanilla,
            fecha_ingreso_interna: fechaIngresoInterna,
            sistema_pensionario: sistemaPensionarioPayload,
            afp_id:
              !esSoloInterna && sistemaPensionarioPayload === 'AFP'
                ? obtenerIdAfp(empleadoData.afp)
                : null,
            cuspp:
              !esSoloInterna && sistemaPensionarioPayload === 'AFP'
                ? (normalizarCuspp(empleadoData.cuspp) || null)
                : null,
            tipo_afp: empleadoData.tipo_afp || 'Flujo',
            hora_entrada: empleadoData.hora_entrada ? empleadoData.hora_entrada.toString().trim() : '08:00',
            hora_salida: empleadoData.hora_salida ? empleadoData.hora_salida.toString().trim() : '17:00',
            cuenta_bancaria: empleadoData.cuenta_bancaria ? empleadoData.cuenta_bancaria.toString().trim() : null,
            banco: empleadoData.banco ? empleadoData.banco.toString().trim() : null,
            cci: empleadoData.cci ? empleadoData.cci.toString().trim() : null,
            contacto_emergencia: empleadoData.contacto_emergencia ? empleadoData.contacto_emergencia.toString().trim() : null,
            telefono_emergencia: empleadoData.telefono_emergencia ? empleadoData.telefono_emergencia.toString().trim() : null,
            observaciones: empleadoData.observaciones ? empleadoData.observaciones.toString().trim() : null,
            fecha_ingreso: fechaIngresoPayload,
            monto_viatico_standard: viaticosStandardActivo
              ? (parseFloat(empleadoData.monto_viatico_standard) || 0)
              : 0,
            conceptos: conceptosParaBackend
          };

          console.log(`📤 Importando empleado: ${empleadoData.nombre_completo}`);
          console.log(`📊 Con ${conceptosParaBackend.length} conceptos total (calculados + Excel):`, conceptosParaBackend);
          console.log(`📋 Datos enviados al backend:`, empleadoParaBackend);
          
          // Llamada real al backend
          const response = await empleadoService.createEmpleado(empleadoParaBackend);
          
          resultados.exitosos.push({
            fila: empleadoData.fila,
            nombre: empleadoData.nombre_completo,
            id: response.data?.id,
            conceptos: {
              total: conceptosParaBackend.length,
              ingresos: conteoConceptos.ingresos,
              descuentos: conteoConceptos.descuentos,
              aportes: conteoConceptos.aportes,
              internos: conteoConceptos.internos,
              conceptosDelExcel: conceptosEmpleadoExcel.length
            }
          });
          
        } catch (error) {
          console.error(`❌ Error importando empleado ${empleadoData.nombre_completo}:`, error);
          resultados.fallidos.push({
            fila: empleadoData.fila,
            nombre: empleadoData.nombre_completo,
            error: error.message || 'Error desconocido'
          });
        }
      }

      setResultadosImportacion(resultados);
      setMostrarResultados(true);
      
      console.log('🎉 Importación REAL completada exitosamente en base de datos:', resultados);
      
      // Si hay empleados exitosos, notificar al componente padre
      if (resultados.exitosos.length > 0 && onImportSuccess) {
        onImportSuccess(resultados);
      }

    } catch (error) {
      console.error('❌ Error general en importación:', error);
    } finally {
      setImportandoEmpleados(false);
    }
  };

  // Funciones auxiliares
  const obtenerIdRegimenLaboral = (nombreRegimen) => {
    if (!nombreRegimen) return null;
    const regimen = datosApis.regimenesLaborales.find(r => r.nombre === nombreRegimen);
    return regimen?.id || null;
  };

  const obtenerIdAfp = (nombreAfp) => {
    if (!nombreAfp) return null;
    const afp = datosApis.afps.find(a => a.nombre === nombreAfp);
    return afp?.id || null;
  };

  // Función para descargar plantilla Excel
  const descargarPlantilla = () => {
    // Datos de ejemplo para la hoja de empleados (con asteriscos en campos obligatorios)
    const plantillaEmpleados = [{
      'nombre_completo *': 'Juan Pérez García',
      'tipo_documento *': 'DNI',
      'numero_documento *': '12345678',
      'fecha_nacimiento *': '1990-01-15',
      'sexo *': 'Masculino',
      'estado_civil *': 'Soltero',
      'numero_hijos *': 0,
      'telefono': '987654321',
      'email': 'juan.perez@email.com',
      'direccion *': 'Av. Ejemplo 123',
      'distrito *': 'Lima',
      'provincia *': 'Lima',
      'departamento *': 'Lima',
      'cargo *': datosApis.atributos.Cargos?.[0]?.nombre || 'Gerente',
      'categoria *': datosApis.atributos.Categorías?.[0]?.nombre || 'Administrativos',
      'area_trabajo *': datosApis.atributos['Area de Trabajo']?.[0]?.nombre || 'Administración',
      'proyecto *': datosApis.atributos.Proyectos?.[0]?.nombre || 'CC1',
      'tipo_contratacion *': TIPOS_CONTRATACION_EMPLEADO[0],
      'estado *': 'Activo',
      'regimen_laboral *': datosApis.regimenesLaborales[0]?.nombre || 'Régimen General',
      ...(viaticosStandardActivo
        ? { 'monto_viatico_standard': 100 }
        : {}),
      ...(planillaAlternativaActiva
        ? {
            'tipo_planilla *': 'dual',
            'sueldo_basico *': 1130,
            'sueldo_interno *': 3000,
            'sistema_pensionario *': 'ONP',
            'fecha_ingreso *': '2024-01-01',
            'fecha_ingreso_interna *': '2024-01-01',
          }
        : {
            'sueldo_basico *': 3000,
            'sistema_pensionario *': 'ONP',
            'fecha_ingreso *': '2024-01-01',
          }),
      'afp': '',
      'tipo_afp': 'Flujo',
      'cuspp': '',
      'hora_entrada *': '08:00',
      'hora_salida': '17:00',
      'cuenta_bancaria': '1234567890',
      'banco': 'BCP',
      'cci': '00211234567890123456',
      'contacto_emergencia': 'María García',
      'telefono_emergencia': '987654322',
      'observaciones': planillaAlternativaActiva
        ? 'Ejemplo dual (PLAME + interna)'
        : 'Empleado de ejemplo',
    }, {
      'nombre_completo *': 'Ana López Silva',
      'tipo_documento *': 'Carnet de Extranjería',
      'numero_documento *': '001234567',
      'fecha_nacimiento *': '1985-06-20',
      'sexo *': 'Femenino',
      'estado_civil *': 'Casado',
      'numero_hijos *': 2,
      'telefono': '912345678',
      'email': 'ana.lopez@email.com',
      'direccion *': 'Jr. Los Pinos 456',
      'distrito *': 'San Isidro',
      'provincia *': 'Lima',
      'departamento *': 'Lima',
      'cargo *': datosApis.atributos.Cargos?.[1]?.nombre || 'Supervisor',
      'categoria *': datosApis.atributos.Categorías?.[0]?.nombre || 'Administrativos',
      'area_trabajo *': datosApis.atributos['Area de Trabajo']?.[1]?.nombre || 'Operaciones',
      'proyecto *': datosApis.atributos.Proyectos?.[0]?.nombre || 'CC1',
      'tipo_contratacion *': planillaAlternativaActiva ? 'Interno' : TIPOS_CONTRATACION_EMPLEADO[0],
      'estado *': 'Activo',
      'regimen_laboral *': datosApis.regimenesLaborales[0]?.nombre || 'Régimen General',
      ...(viaticosStandardActivo
        ? { 'monto_viatico_standard': 150 }
        : {}),
      ...(planillaAlternativaActiva
        ? {
            'tipo_planilla *': 'solo_interna',
            'sueldo_basico': '',
            'sueldo_interno *': 2500,
            'sistema_pensionario': '',
            'afp': '',
            'tipo_afp': '',
            'cuspp': '',
            'fecha_ingreso': '',
            'fecha_ingreso_interna *': '2024-02-01',
          }
        : {
            'sueldo_basico *': 2500,
            'sistema_pensionario *': 'AFP',
            'afp *': datosApis.afps[0]?.nombre || 'INTEGRA',
            'tipo_afp *': 'Flujo',
            'cuspp *': '1234567890123',
            'fecha_ingreso *': '2024-01-01',
          }),
      'hora_entrada *': '08:30',
      'hora_salida': '17:30',
      'cuenta_bancaria': '0987654321',
      'banco': 'BBVA',
      'cci': '01198765432109876543',
      'contacto_emergencia': 'Carlos López',
      'telefono_emergencia': '987654323',
      'observaciones': planillaAlternativaActiva
        ? 'Ejemplo solo interna (sin ONP/AFP ni aportes)'
        : 'Segundo empleado de ejemplo',
    }];

    // Datos de ejemplo para conceptos (numero_documento = DNI o Carnet de Extranjería)
    const plantillaConceptosPlame = [
      {
        'numero_documento *': '12345678',
        'codigo_plame *': datosApis.conceptosPlame[0]?.codigo || '0101',
        'monto *': 500,
      },
      {
        'numero_documento *': '12345678',
        'codigo_plame *': datosApis.conceptosPlame[1]?.codigo || '0102',
        'monto *': 300,
      },
    ];

    const plantillaConceptosInterna = [
      {
        'numero_documento *': '12345678',
        'codigo_plame *': datosApis.conceptosPlame[0]?.codigo || '0101',
        'monto *': 200,
      },
      {
        'numero_documento *': '001234567',
        'codigo_plame *': datosApis.conceptosPlame[0]?.codigo || '0101',
        'monto *': 400,
      },
    ];

    const plantillaConceptosClasico = [
      {
        'numero_documento *': '12345678',
        'codigo_plame *': datosApis.conceptosPlame[0]?.codigo || '0100',
        'monto *': 500,
      },
      {
        'numero_documento *': '12345678',
        'codigo_plame *': datosApis.conceptosPlame[1]?.codigo || '0200',
        'monto *': 300,
      },
      {
        'numero_documento *': '001234567',
        'codigo_plame *': datosApis.conceptosPlame[0]?.codigo || '0100',
        'monto *': 400,
      },
      {
        'numero_documento *': '001234567',
        'codigo_plame *': datosApis.conceptosPlame[2]?.codigo || '0300',
        'monto *': 250,
      },
    ];

    // Crear el workbook
    const wb = XLSX.utils.book_new();
    
    // Crear y agregar hoja de empleados
    const wsEmpleados = XLSX.utils.json_to_sheet(plantillaEmpleados);
    XLSX.utils.book_append_sheet(wb, wsEmpleados, 'Empleados');
    
    if (planillaAlternativaActiva) {
      const wsPlame = XLSX.utils.json_to_sheet(plantillaConceptosPlame);
      XLSX.utils.book_append_sheet(wb, wsPlame, 'Conceptos_PLAME');
      const wsInterna = XLSX.utils.json_to_sheet(plantillaConceptosInterna);
      XLSX.utils.book_append_sheet(wb, wsInterna, 'Conceptos_Interna');
    } else {
      const wsConceptos = XLSX.utils.json_to_sheet(plantillaConceptosClasico);
      XLSX.utils.book_append_sheet(wb, wsConceptos, 'Conceptos');
    }
    
    // Descargar el archivo
    XLSX.writeFile(wb, 'plantilla_importacion_empleados.xlsx');
    
    console.log('📁 Plantilla descargada: plantilla_importacion_empleados.xlsx');
    if (planillaAlternativaActiva) {
      console.log('📋 Hojas: Empleados | Conceptos_PLAME | Conceptos_Interna');
      console.log('🔀 Planilla alternativa ACTIVA — columnas: tipo_planilla*, sueldo_interno*, fecha_ingreso_interna*');
      console.log('   dual: exige sueldo_basico + fecha_ingreso + sistema_pensionario');
      console.log('   solo_interna: solo sueldo_interno + fecha_ingreso_interna (sin ONP/AFP ni aportes)');
      console.log('   Conceptos_PLAME → boleta oficial; Conceptos_Interna → boleta de control');
    } else {
      console.log('📋 Hojas: Empleados | Conceptos');
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-5 border max-w-6xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900">Importar Empleados</h3>
              <p className="text-gray-600 mt-1">Importar empleados desde archivo Excel</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Estado de carga de datos */}
          {cargandoDatos && (
            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
                <h4 className="text-lg font-medium text-blue-900">Cargando datos del sistema...</h4>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2 mb-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                  style={{ width: `${progresoCarga}%` }}
                ></div>
              </div>
              <p className="text-blue-700 text-sm">{progresoCarga}% completado</p>
            </div>
          )}

          {/* Sección principal cuando los datos están listos */}
          {datosListos && (
            <>
              {/* Información de datos cargados */}
              <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <h4 className="text-lg font-medium text-green-900">Datos del sistema cargados</h4>
                </div>
                <div className="flex items-center justify-end mb-3">
                  <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    ✅ 8/8 APIs cargadas
                  </div>
                </div>
                <div className="border-t border-green-200 pt-3 text-xs text-green-700">
                  <p className="font-medium mb-1">📋 Campos obligatorios en tu archivo Excel:</p>
                  <div className="grid grid-cols-1 gap-2">
                    <div>
                      <span className="font-medium">Hoja Empleados:</span> nombre_completo*, tipo_documento*, numero_documento*, fecha_nacimiento*, sexo*, estado_civil*, numero_hijos*, direccion*, distrito*, provincia*, departamento*, cargo*, categoria*, area_trabajo*, proyecto*, tipo_contratacion*, estado*, regimen_laboral*, hora_entrada*
                      {planillaAlternativaActiva ? (
                        <>
                          , <span className="text-indigo-700 font-medium">tipo_planilla*</span> (dual | solo_interna), <span className="text-indigo-700 font-medium">sueldo_interno*</span>, <span className="text-indigo-700 font-medium">fecha_ingreso_interna*</span>
                          <div className="text-xs text-indigo-700 mt-1">
                            <span className="font-medium">dual:</span> también sueldo_basico*, fecha_ingreso*, sistema_pensionario* (ONP/AFP → mismos descuentos/aportes en ambas boletas)
                          </div>
                          <div className="text-xs text-amber-700 mt-1">
                            <span className="font-medium">solo_interna:</span> sin exigir sueldo PLAME ni pensión; conceptos solo ingresos (sin ONP/AFP ni aportes)
                          </div>
                        </>
                      ) : (
                        <>, sueldo_basico*, sistema_pensionario*, fecha_ingreso*</>
                      )}
                      <div className="text-xs text-blue-600 mt-1">
                        <span className="font-medium">Si AFP:</span> afp*, tipo_afp*, cuspp* (máximo 12 caracteres alfanuméricos, solo obligatorios cuando sistema_pensionario = AFP)
                      </div>
                      <div className="text-xs text-green-600 mt-1">
                        <span className="font-medium">Opcionales:</span> telefono, email (se valida formato si se proporcionan)
                      </div>
                    </div>
                    <div>
                      {planillaAlternativaActiva ? (
                        <>
                          <span className="font-medium">Hojas de conceptos:</span>{' '}
                          <span className="text-slate-800 font-medium">Conceptos_PLAME</span> (boleta oficial) y{' '}
                          <span className="text-amber-800 font-medium">Conceptos_Interna</span> (boleta control).
                          Ambas: numero_documento* (DNI o Carnet de Extranjería), codigo_plame*, monto*
                          <div className="text-xs text-amber-700 mt-1">
                            dual → use ambas hojas según corresponda · solo_interna → solo Conceptos_Interna
                          </div>
                        </>
                      ) : (
                        <span className="font-medium">Hoja Conceptos:</span>
                      )}
                      {!planillaAlternativaActiva && (
                        <> numero_documento* (DNI o Carnet de Extranjería), codigo_plame*, monto* (se clasifican automáticamente según el tipo PLAME)</>
                      )}
                    </div>
                  </div>
                  {planillaAlternativaActiva && (
                    <div className="mt-2 rounded-md bg-indigo-50 border border-indigo-200 px-3 py-2 text-indigo-800">
                      Planilla alternativa activa: la plantilla Excel incluye columnas dual / solo_interna.
                    </div>
                  )}
                </div>
              </div>

              {/* Sección de carga de archivo */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">Seleccionar archivo Excel</h4>
                    <div className="text-sm text-gray-600 mt-1 space-y-1">
                      <p>El archivo debe tener 2 hojas: "Empleados" y "Conceptos"</p>
                      <p className="text-xs">Los campos obligatorios están marcados con asterisco (*) en la plantilla</p>
                      <p className="flex items-center">
                        <span className="text-red-500 mr-1">*</span>
                        Los campos marcados con asterisco son obligatorios
                      </p>
                      <p className="flex items-center text-green-600">
                        <span className="text-green-500 mr-1">📞</span>
                        telefono y email son opcionales (se valida formato si se proporcionan)
                      </p>
                      <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-2">
                        <p className="text-xs text-blue-700 flex items-center">
                          <span className="mr-1">🧮</span>
                          Los conceptos se calcularán automáticamente según la configuración del empleado
                        </p>
                        <p className="text-xs text-blue-700 flex items-center mt-1">
                          <span className="mr-1">🏷️</span>
                          Los conceptos se clasifican automáticamente como ingresos, descuentos o aportes según su código PLAME
                        </p>
                      </div>
                      <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mt-2">
                        <p className="text-xs text-yellow-700 flex items-center">
                          <span className="mr-1">⚠️</span>
                          Los campos AFP, tipo_afp y cuspp (máximo 12 caracteres alfanuméricos) solo son obligatorios cuando sistema_pensionario = "AFP"
                        </p>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={descargarPlantilla}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Descargar Plantilla (2 hojas)
                  </button>
                </div>

                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  
                  {!archivo ? (
                    <div>
                      <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <h5 className="text-lg font-medium text-gray-900 mb-2">
                        Selecciona un archivo Excel
                      </h5>
                      <p className="text-gray-500 mb-4">
                        Soporta archivos .xlsx y .xls
                      </p>
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Seleccionar archivo
                      </button>
                    </div>
                  ) : (
                    <div>
                      <svg className="w-12 h-12 mx-auto mb-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <h5 className="text-lg font-medium text-gray-900 mb-2">
                        {archivo.name}
                      </h5>
                      <p className="text-gray-500 mb-4">
                        {(archivo.size / 1024).toFixed(2)} KB
                      </p>
                      <div className="flex justify-center space-x-3">
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Cambiar archivo
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {procesandoArchivo && (
                  <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-yellow-600 mr-3"></div>
                      <span className="text-yellow-800">Procesando archivo Excel...</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Sección de validaciones */}
              {mostrarValidaciones && (
                <div className="mb-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Resultado de Validaciones</h4>
                  
                  {/* Resumen */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {/* Resumen Empleados */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <h5 className="font-medium text-gray-900 mb-3">📋 Hoja de Empleados</h5>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="bg-blue-50 border border-blue-200 rounded p-2">
                          <div className="text-lg font-bold text-blue-600">{datosExcel.length}</div>
                          <div className="text-xs text-blue-700">Total</div>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded p-2">
                          <div className="text-lg font-bold text-green-600">{empleadosValidos.length}</div>
                          <div className="text-xs text-green-700">Válidos</div>
                        </div>
                        <div className="bg-red-50 border border-red-200 rounded p-2">
                          <div className="text-lg font-bold text-red-600">{erroresValidacion.length}</div>
                          <div className="text-xs text-red-700">Errores</div>
                        </div>
                      </div>
                    </div>

                    {/* Resumen Conceptos */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <h5 className="font-medium text-gray-900 mb-3">💰 Hoja de Conceptos</h5>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="bg-blue-50 border border-blue-200 rounded p-2">
                          <div className="text-lg font-bold text-blue-600">{conceptosExcel.length}</div>
                          <div className="text-xs text-blue-700">Total</div>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded p-2">
                          <div className="text-lg font-bold text-green-600">{conceptosValidos.length}</div>
                          <div className="text-xs text-green-700">Válidos</div>
                        </div>
                        <div className="bg-red-50 border border-red-200 rounded p-2">
                          <div className="text-lg font-bold text-red-600">{erroresValidacionConceptos.length}</div>
                          <div className="text-xs text-red-700">Errores</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Lista de errores */}
                  {(erroresValidacion.length > 0 || erroresValidacionConceptos.length > 0) && (
                    <div className="mb-6 space-y-4">
                      {/* Errores de empleados */}
                      {erroresValidacion.length > 0 && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <h5 className="font-medium text-red-900 mb-3">❌ Errores en hoja de Empleados:</h5>
                          <div className="max-h-64 overflow-y-auto space-y-3">
                            {erroresValidacion.map((error, index) => (
                              <div key={index} className="bg-white border border-red-200 rounded p-3">
                                <div className="font-medium text-red-800">
                                  Fila {error.fila}: {error.empleado}
                                </div>
                                <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                                  {error.errores.map((err, errIndex) => (
                                    <li key={errIndex}>{err}</li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Errores de conceptos */}
                      {erroresValidacionConceptos.length > 0 && (
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                          <h5 className="font-medium text-orange-900 mb-3">⚠️ Errores en hoja de Conceptos:</h5>
                          <div className="max-h-64 overflow-y-auto space-y-3">
                            {erroresValidacionConceptos.map((error, index) => (
                              <div key={index} className="bg-white border border-orange-200 rounded p-3">
                                <div className="font-medium text-orange-800">
                                  Fila {error.fila}: Doc. {error.dni} - Concepto {error.concepto}
                                </div>
                                <ul className="mt-2 text-sm text-orange-700 list-disc list-inside">
                                  {error.errores.map((err, errIndex) => (
                                    <li key={errIndex}>{err}</li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Botón para importar empleados válidos */}
                  {empleadosValidos.length > 0 && (
                    <div className="flex flex-col items-center space-y-4">
                      {/* Información de importación */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                        <h6 className="font-medium text-blue-900 mb-2">🚀 Resumen de Importación</h6>
                        <div className="text-sm text-blue-800 space-y-1">
                          <div>✅ {empleadosValidos.length} empleados listos para importar</div>
                          <div>🧮 Conceptos se calcularán automáticamente según configuración</div>
                          <div>📋 {conceptosValidos.length} conceptos adicionales del Excel (clasificados automáticamente)</div>
                          <div className="bg-green-100 border border-green-300 rounded p-2 mt-2">
                            <span className="text-green-800 font-medium">🚀 IMPORTACIÓN REAL: </span>
                            <span className="text-green-700">Los empleados se crearán en la base de datos con todos sus conceptos</span>
                          </div>
                          {conceptosExcel.length > 0 && erroresValidacionConceptos.length > 0 && (
                            <div className="text-orange-700">
                              ⚠️ {erroresValidacionConceptos.length} conceptos del Excel serán omitidos por errores
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Botón de importar */}
                      <button
                        onClick={importarEmpleados}
                        disabled={importandoEmpleados || (erroresValidacion.length > 0)}
                        className="inline-flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {importandoEmpleados ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                            Importando empleados a la base de datos...
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                            </svg>
                            🚀 Importar {empleadosValidos.length} empleados con conceptos calculados
                          </>
                        )}
                      </button>

                      {/* Nota sobre errores */}
                      {erroresValidacion.length > 0 && (
                        <div className="text-sm text-red-600 text-center">
                          ❌ Corrige los errores de empleados antes de importar
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Resultados de importación */}
              {mostrarResultados && resultadosImportacion && (
                <div className="mb-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">✅ Resultados de Importación en Base de Datos</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Empleados importados exitosamente */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h5 className="font-medium text-green-900 mb-3">
                        Importados exitosamente ({resultadosImportacion.exitosos.length})
                      </h5>
                      <div className="max-h-40 overflow-y-auto space-y-2">
                        {resultadosImportacion.exitosos.map((emp, index) => (
                          <div key={index} className="text-sm text-green-700">
                            <span className="font-medium">Fila {emp.fila}:</span> {emp.nombre}
                            {typeof emp.conceptos === 'object' ? (
                              <div className="text-green-600 ml-2 mt-1">
                                <div>📊 {emp.conceptos.total} conceptos total</div>
                                <div className="text-xs">
                                  🧮 {emp.conceptos.calculados} automáticos + 📋 {emp.conceptos.excel} Excel
                                </div>
                              </div>
                            ) : emp.conceptos > 0 ? (
                              <span className="text-green-600 ml-2">({emp.conceptos} conceptos)</span>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Empleados con errores */}
                    {resultadosImportacion.fallidos.length > 0 && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <h5 className="font-medium text-red-900 mb-3">
                          Fallidos ({resultadosImportacion.fallidos.length})
                        </h5>
                        <div className="max-h-40 overflow-y-auto space-y-2">
                          {resultadosImportacion.fallidos.map((emp, index) => (
                            <div key={index} className="text-sm">
                              <div className="font-medium text-red-800">Fila {emp.fila}: {emp.nombre}</div>
                              <div className="text-red-600">{emp.error}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Botones del footer */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportarEmpleados; 